import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useProfile } from "../context/ProfileContext";
import { Mail, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const PROFILE = useProfile();
  const [form, setForm] = useState({
    name: "",
    email: "",
    school: "",
    role: "Head Coach",
    division: "D1",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.school || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message received. Faith will follow up soon.");
      setForm({ name: "", email: "", school: "", role: "Head Coach", division: "D1", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please email directly.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
            07 — Coach Contact
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-bold tracking-tight text-white leading-none">
            Let's Talk.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* References */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 p-8" data-testid="direct-contact">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
                Direct Inbox
              </div>
              <div className="font-display text-xl uppercase font-bold tracking-tight text-white mb-2">
                Faith Ahyi-Sena
              </div>
              <a
                href={`mailto:${PROFILE.email}`}
                className="inline-flex items-center gap-2 text-sm text-white hover:text-[#E60000] transition-colors break-all"
                data-testid="email-faith"
              >
                <Mail size={14} />
                {PROFILE.email}
              </a>
            </div>

            <div className="border border-white/10 p-8" data-testid="coach-westminster">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
                High School Reference
              </div>
              <div className="font-display text-2xl uppercase font-bold tracking-tight text-white">
                {PROFILE.schoolCoach.name}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A8A] mt-1 mb-3">
                {PROFILE.schoolCoach.title}
              </div>
              <a
                href={`mailto:${PROFILE.schoolCoach.email}`}
                className="inline-flex items-center gap-2 text-sm text-white hover:text-[#E60000] transition-colors break-all"
              >
                <Mail size={14} /> {PROFILE.schoolCoach.email}
              </a>
            </div>

            <div className="border border-white/10 p-8" data-testid="coach-club">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
                Club Reference
              </div>
              <div className="font-display text-2xl uppercase font-bold tracking-tight text-white">
                {PROFILE.clubCoach.name}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A8A] mt-1 mb-3">
                {PROFILE.clubCoach.title}
              </div>
              <a
                href={`mailto:${PROFILE.clubCoach.email}`}
                className="inline-flex items-center gap-2 text-sm text-white hover:text-[#E60000] transition-colors break-all"
              >
                <Mail size={14} /> {PROFILE.clubCoach.email}
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 border border-white/10 p-8 lg:p-10 space-y-6"
            data-testid="contact-form"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-2">
              Reach Out to Faith
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Coach Name *" testid="field-name">
                <input
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Coach Smith"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#E60000]"
                  data-testid="input-name"
                />
              </Field>
              <Field label="Email *" testid="field-email">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="coach@university.edu"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#E60000]"
                  data-testid="input-email"
                />
              </Field>
            </div>

            <Field label="College / University *" testid="field-school">
              <input
                required
                value={form.school}
                onChange={onChange("school")}
                placeholder="University of ..."
                className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#E60000]"
                data-testid="input-school"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Role" testid="field-role">
                <select
                  value={form.role}
                  onChange={onChange("role")}
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm focus:border-[#E60000] appearance-none"
                  data-testid="input-role"
                >
                  {["Head Coach", "Associate Head Coach", "Assistant Coach", "Recruiting Coordinator", "Other"].map((r) => (
                    <option key={r} value={r} className="bg-[#0A0A0A]">{r}</option>
                  ))}
                </select>
              </Field>
              <Field label="Division" testid="field-division">
                <select
                  value={form.division}
                  onChange={onChange("division")}
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm focus:border-[#E60000] appearance-none"
                  data-testid="input-division"
                >
                  {["D1", "D2", "D3", "NAIA", "JUCO", "Other"].map((d) => (
                    <option key={d} value={d} className="bg-[#0A0A0A]">{d}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Message *" testid="field-message">
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Hi Faith — we'd love to learn more about your timeline, upcoming tournaments, and academic interests..."
                className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#E60000] resize-none"
                data-testid="input-message"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-60 text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-colors"
              data-testid="submit-contact"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />}
              {submitting ? "Sending..." : "Send to Faith"}
            </button>

            <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A] pt-2">
              Faith and her family will respond within 48 hours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-2 font-bold">
        {label}
      </span>
      {children}
    </label>
  );
}
